/**
 * Sentry-aware companion to `correlation.ts`.
 *
 * Generalizes the previously host-only trace-attach so every surface
 * (host-frontend / player clients / admin) can both propagate the active
 * Sentry trace and stamp hierarchical IDs from a single place. Uses
 * `@sentry/core`, which every SDK (browser/react/node) re-exports from the
 * same global carrier, so the active span resolves correctly on all surfaces.
 */

import { getActiveSpan, spanToTraceHeader, spanToBaggageHeader } from '@sentry/core';
import { attachCorrelationToMessage } from './correlation';
import type { CorrelatableMessage, CorrelationIds } from './correlation';

/**
 * Attach the active Sentry trace + baggage headers to a message so the
 * receiving service can continue the distributed trace. No-op when there is
 * no active span. Mutates in place and returns the same reference.
 */
export function attachSentryTraceToMessage<T extends CorrelatableMessage>(message: T): T {
  const activeSpan = getActiveSpan();
  if (!activeSpan) {
    return message;
  }

  const traceHeader = spanToTraceHeader(activeSpan);
  const baggageHeader = spanToBaggageHeader(activeSpan);

  if (traceHeader) {
    message._sentryTrace = traceHeader;
  }
  if (baggageHeader) {
    message._sentryBaggage = baggageHeader;
  }

  return message;
}

/**
 * One-stop helper: stamp hierarchical IDs and propagate the active trace.
 * This is the canonical call for every WebSocket sender chokepoint.
 */
export function attachObservabilityToMessage<T extends CorrelatableMessage>(
  message: T,
  ids: CorrelationIds,
): T {
  attachCorrelationToMessage(message, ids);
  attachSentryTraceToMessage(message);
  return message;
}
